// src/components/forms/formSteps/PersonalInfoStep.tsx
import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";

interface PersonalInfoStepProps {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void;
  isMobile: boolean;
}

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  firstName,
  lastName,
  phone,
  email,
  validation,
  onChange,
  onSubmit,
  isMobile,
}) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [hasPrefilled, setHasPrefilled] = useState(false);

  // Extract URL parameters and prefill form on component mount (only once)
  useEffect(() => {
    // Only run prefilling once
    if (hasPrefilled) return;

    const urlFirstName = searchParams.get("fname");
    const urlLastName = searchParams.get("lname");
    const urlPhone = searchParams.get("phone");
    const urlEmail = searchParams.get("email");

    // Only prefill if URL has values and current fields are empty
    if (urlFirstName && firstName.trim() === "") {
      onChange("firstName", urlFirstName);
    }
    if (urlLastName && lastName.trim() === "") {
      onChange("lastName", urlLastName);
    }
    if (urlPhone && phone.trim() === "") {
      onChange("phone", urlPhone);
    }
    if (urlEmail && email.trim() === "") {
      onChange("email", urlEmail);
    }

    // Mark as prefilled to prevent running again
    setHasPrefilled(true);
  }, [searchParams, hasPrefilled, firstName, lastName, phone, email, onChange]);

  // Handle keydown event - only prevent default, don't auto-submit
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  // Check if all required fields are filled and validation passes
  const isFormValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    phone.trim() !== "" &&
    email.trim() !== "" &&
    validation.valid;

  return (
    <>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        align="center"
        sx={{ color: "#c6c6c6", mb: 2 }}
      >
        {t("form.sixthQuestion.title")}
      </Typography>
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        {t("form.personalInfo.title")}
      </Typography>

      <Box sx={{ maxWidth: 500, mx: "auto" }}>
        {/* First Name */}
        <Grid container alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="First Name"
              fullWidth
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => onChange("firstName", e.target.value)}
              onKeyDown={handleKeyDown}
              error={!validation.valid && firstName.trim() === ""}
              helperText={
                firstName.length > 0 ? `${firstName.length}/63 characters` : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  textAlign: "left",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Last Name */}
        <Grid container alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="Last Name"
              fullWidth
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => onChange("lastName", e.target.value)}
              onKeyDown={handleKeyDown}
              error={!validation.valid && lastName.trim() === ""}
              helperText={
                lastName.length > 0 ? `${lastName.length}/63 characters` : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  textAlign: "left",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Phone */}
        <Grid container alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter your phone number"
              value={phone}
              onChange={(e) => onChange("phone", e.target.value)}
              onKeyDown={handleKeyDown}
              error={!validation.valid && phone.trim() === ""}
              helperText={
                phone.length > 0 ? `${phone.length}/20 characters` : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  textAlign: "left",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Email */}
        <Grid container alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Email Address"
              fullWidth
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => onChange("email", e.target.value)}
              onKeyDown={handleKeyDown}
              error={!validation.valid && email.trim() === ""}
              helperText={
                email.length > 0 ? `${email.length}/100 characters` : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  textAlign: "left",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Display validation error if any */}
      {!validation.valid && validation.error && (
        <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
          {validation.error}
        </Typography>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={!isFormValid}
          sx={{
            minWidth: "200px",
            py: 1.5,
            fontWeight: "bold",
            backgroundColor: "#ff6b35",
            "&:hover": {
              backgroundColor: "#e25a2a",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </>
  );
};

export default PersonalInfoStep;
